import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
    Select, MenuItem, Chip, Switch, FormControlLabel, Divider, Card, CardContent
} from '@mui/material';
import { Add, Delete, Edit, Schedule, Save, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const TimetableSettings = () => {
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');
    const [settings, setSettings] = useState({
        totalWorkingDays: 220,
        periodsPerDay: 8,
        halfDayPeriods: 4,
        defaultPeriodDuration: 40,
        breakDuration: 15,
        lunchDuration: 30,
        schoolStartTime: '08:00',
        schoolEndTime: '14:30',
        halfDayEndTime: '11:30',
        workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        shifts: ['Morning']
    });
    const [periods, setPeriods] = useState([]);
    const [dayType, setDayType] = useState('full');
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editPeriod, setEditPeriod] = useState(null);
    const [periodForm, setPeriodForm] = useState({
        periodName: '',
        startTime: '',
        endTime: '',
        periodType: 'lecture'
    });

    const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const PERIOD_TYPES = ['lecture', 'break', 'lunch', 'assembly', 'other'];

    useEffect(() => {
        fetchAcademicYears();
    }, []);

    useEffect(() => {
        if (selectedYear) {
            fetchSettings();
            fetchPeriods();
        }
    }, [selectedYear, dayType]);

    const fetchAcademicYears = async () => {
        try {
            const res = await api.get('/academic-years');
            setAcademicYears(res.data.data || []);
            const current = res.data.data?.find(y => y.isCurrent) || res.data.data?.[0];
            if (current) setSelectedYear(current.id);
        } catch (error) {
            console.error('Failed to fetch academic years');
        }
    };

    const fetchSettings = async () => {
        try {
            const res = await api.get('/timetable/settings', { params: { academicYearId: selectedYear } });
            if (res.data.data) setSettings(res.data.data);
        } catch (error) {
            console.error('Failed to fetch settings');
        }
    };

    const fetchPeriods = async () => {
        try {
            const res = await api.get('/timetable/periods', {
                params: { academicYearId: selectedYear, dayType }
            });
            setPeriods(res.data.data || []);
        } catch (error) {
            console.error('Failed to fetch periods');
        }
    };

    const handleSaveSettings = async () => {
        setLoading(true);
        try {
            await api.post('/timetable/settings', { ...settings, academicYearId: selectedYear });
            toast.success('Settings saved successfully');
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setLoading(false);
        }
    };

    const handleAutoGenerate = async () => {
        setLoading(true);
        try {
            await api.post('/timetable/periods/auto-generate', {
                academicYearId: selectedYear,
                dayType
            });
            toast.success('Periods generated successfully');
            fetchPeriods();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to generate periods');
        } finally {
            setLoading(false);
        }
    };

    const handleAddPeriod = () => {
        setEditPeriod(null);
        setPeriodForm({ periodName: '', startTime: '', endTime: '', periodType: 'lecture' });
        setOpenDialog(true);
    };

    const handleEditPeriod = (period) => {
        setEditPeriod(period);
        setPeriodForm({
            periodName: period.periodName,
            startTime: period.startTime?.slice(0, 5),
            endTime: period.endTime?.slice(0, 5),
            periodType: period.periodType
        });
        setOpenDialog(true);
    };

    const handleSavePeriod = async () => {
        try {
            const data = {
                ...periodForm,
                academicYearId: selectedYear,
                dayType,
                periodNumber: editPeriod ? editPeriod.periodNumber : periods.length + 1
            };

            if (editPeriod) {
                await api.put(`/timetable/periods/${editPeriod.id}`, data);
                toast.success('Period updated');
            } else {
                await api.post('/timetable/periods', data);
                toast.success('Period added');
            }
            setOpenDialog(false);
            fetchPeriods();
        } catch (error) {
            toast.error('Failed to save period');
        }
    };

    const handleDeletePeriod = async (id) => {
        if (!window.confirm('Delete this period?')) return;
        try {
            await api.delete(`/timetable/periods/${id}`);
            toast.success('Period deleted');
            fetchPeriods();
        } catch (error) {
            toast.error('Failed to delete period');
        }
    };

    const handleWorkingDayToggle = (day) => {
        setSettings(prev => ({
            ...prev,
            workingDays: prev.workingDays.includes(day)
                ? prev.workingDays.filter(d => d !== day)
                : [...prev.workingDays, day]
        }));
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Timetable Settings</Typography>

            {/* Academic Year & Day Type Selection */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Academic Year</InputLabel>
                            <Select value={selectedYear} label="Academic Year" onChange={e => setSelectedYear(e.target.value)}>
                                {academicYears.map(y => (
                                    <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                                label="Full Day"
                                color={dayType === 'full' ? 'primary' : 'default'}
                                onClick={() => setDayType('full')}
                            />
                            <Chip
                                label="Half Day"
                                color={dayType === 'half' ? 'primary' : 'default'}
                                onClick={() => setDayType('half')}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3}>
                {/* Settings Panel */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>General Settings</Typography>
                        <Divider sx={{ mb: 2 }} />

                        <TextField
                            fullWidth size="small" label="Total Working Days" type="number"
                            value={settings.totalWorkingDays} margin="dense"
                            onChange={e => setSettings({ ...settings, totalWorkingDays: parseInt(e.target.value) })}
                        />
                        <TextField
                            fullWidth size="small" label="Periods Per Day (Full)" type="number"
                            value={settings.periodsPerDay} margin="dense"
                            onChange={e => setSettings({ ...settings, periodsPerDay: parseInt(e.target.value) })}
                        />
                        <TextField
                            fullWidth size="small" label="Periods (Half Day)" type="number"
                            value={settings.halfDayPeriods} margin="dense"
                            onChange={e => setSettings({ ...settings, halfDayPeriods: parseInt(e.target.value) })}
                        />
                        <TextField
                            fullWidth size="small" label="Period Duration (min)" type="number"
                            value={settings.defaultPeriodDuration} margin="dense"
                            onChange={e => setSettings({ ...settings, defaultPeriodDuration: parseInt(e.target.value) })}
                        />
                        <TextField
                            fullWidth size="small" label="Break Duration (min)" type="number"
                            value={settings.breakDuration} margin="dense"
                            onChange={e => setSettings({ ...settings, breakDuration: parseInt(e.target.value) })}
                        />
                        <TextField
                            fullWidth size="small" label="Lunch Duration (min)" type="number"
                            value={settings.lunchDuration} margin="dense"
                            onChange={e => setSettings({ ...settings, lunchDuration: parseInt(e.target.value) })}
                        />

                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2">School Timings</Typography>

                        <TextField
                            fullWidth size="small" label="Start Time" type="time"
                            value={settings.schoolStartTime} margin="dense"
                            onChange={e => setSettings({ ...settings, schoolStartTime: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth size="small" label="End Time (Full Day)" type="time"
                            value={settings.schoolEndTime} margin="dense"
                            onChange={e => setSettings({ ...settings, schoolEndTime: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth size="small" label="End Time (Half Day)" type="time"
                            value={settings.halfDayEndTime} margin="dense"
                            onChange={e => setSettings({ ...settings, halfDayEndTime: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />

                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2">Working Days</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {DAYS.map(day => (
                                <Chip
                                    key={day}
                                    label={day.slice(0, 3)}
                                    size="small"
                                    color={settings.workingDays?.includes(day) ? 'primary' : 'default'}
                                    onClick={() => handleWorkingDayToggle(day)}
                                />
                            ))}
                        </Box>

                        <Button
                            fullWidth variant="contained" sx={{ mt: 2 }}
                            onClick={handleSaveSettings} disabled={loading}
                            startIcon={<Save />}
                        >
                            Save Settings
                        </Button>
                    </Paper>
                </Grid>

                {/* Periods Panel */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">
                                Periods ({dayType === 'full' ? 'Full Day' : 'Half Day'})
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={handleAutoGenerate}>
                                    Auto Generate
                                </Button>
                                <Button size="small" variant="contained" startIcon={<Add />} onClick={handleAddPeriod}>
                                    Add Period
                                </Button>
                            </Box>
                        </Box>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Start Time</TableCell>
                                        <TableCell>End Time</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {periods.map((p, idx) => (
                                        <TableRow key={p.id} sx={{
                                            backgroundColor: p.periodType === 'break' ? '#fff3e0' :
                                                p.periodType === 'lunch' ? '#e8f5e9' : 'inherit'
                                        }}>
                                            <TableCell>{idx + 1}</TableCell>
                                            <TableCell>{p.periodName}</TableCell>
                                            <TableCell>{p.startTime?.slice(0, 5)}</TableCell>
                                            <TableCell>{p.endTime?.slice(0, 5)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={p.periodType}
                                                    size="small"
                                                    color={p.periodType === 'lecture' ? 'primary' :
                                                        p.periodType === 'break' ? 'warning' : 'success'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => handleEditPeriod(p)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDeletePeriod(p.id)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {periods.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                No periods configured. Click "Auto Generate" to create periods from settings.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Period Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>{editPeriod ? 'Edit Period' : 'Add Period'}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth label="Period Name" margin="normal"
                        value={periodForm.periodName}
                        onChange={e => setPeriodForm({ ...periodForm, periodName: e.target.value })}
                    />
                    <TextField
                        fullWidth label="Start Time" type="time" margin="normal"
                        value={periodForm.startTime}
                        onChange={e => setPeriodForm({ ...periodForm, startTime: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth label="End Time" type="time" margin="normal"
                        value={periodForm.endTime}
                        onChange={e => setPeriodForm({ ...periodForm, endTime: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={periodForm.periodType}
                            label="Type"
                            onChange={e => setPeriodForm({ ...periodForm, periodType: e.target.value })}
                        >
                            {PERIOD_TYPES.map(t => (
                                <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSavePeriod}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TimetableSettings;
